/**
 * SDK Shadow DOM H5/移动端样式(独立于 PC 的 SDK_STYLE)
 *
 * 设计:整体包一层 @media (max-width: 640px),PC 不匹配 -> 零影响,PC 样式块原样不动。
 * 与 PC 同 DOM 结构,纯 CSS 重排(无 JS 分支):
 *  - 贴边:.hh-root padding 0,各区域自带内边距,不叠层
 *  - 房型卡:横向 96×96 方图缩略图(固定高度,不随套餐列表拉伸)+ 内容四行(标题/标签/价格/按钮),各独占一行不堆叠
 *  - 日历:底部弹层 + 暗色遮罩(点击关闭)+ 拖拽条 + 关闭按钮 + 滑入/滑出动画;
 *    三月竖向轮播(裁剪只露当月,滑动切月),对齐 /hub/packages 页面 Sheet
 *  - 价格"每间每晚"单位 H5 显示为价格数字后的小字内联(¥599 每间每晚),避开券后价徽标
 * 拼接注入见 react.tsx(SDK_STYLE + SDK_STYLE_H5),render.ts 同步。
 * 类名统一 hh- 前缀(Shadow DOM 已隔离,前缀仅为可读性)。
 */
export const SDK_STYLE_H5 = `
  @media (max-width: 640px) {
    /* ===== 贴边:根去外边距,各区域自带内边距 ===== */
    .hh-root { padding: 0; }

    /* 页面头:自带横向内边距,不顶边 */
    .hh-header {
      gap: 12px;
      margin-bottom: 16px;
      padding: 16px 0 0;
      flex-wrap: wrap;
    }
    .hh-header__title { font-size: 18px; font-weight: 400; flex: 0 0 100%; padding: 0 12px; }

    /* 营销横幅:全宽贴边 */
    .hh-banner { margin: 16px 0 0; padding: 0 10px; }
    .hh-banner__img { border-radius: 0; }

    /* 日期 pill:紧凑并允许折行;日期格式 PC 年月日 / H5 月日(DateBar 内 isMobile 切换 formatYMD/formatMD) */
    .hh-datebar { min-width: 0; max-width: 100%; }
    .hh-pill {
      padding: 12px 12px;
      gap: 6px;
      font-size: 13px;
      flex-wrap: wrap;
      min-width: 0;
      max-width: 100%;
    }
    /* 标题与日期栏各占一行(flex:0 0 100% 强制换行);日期栏左右撑满,整条可点 */
    .hh-header .hh-datebar { width: 100%; }
    .hh-header .hh-cal-wrap { width: 100%; }
    .hh-header .hh-pill { padding: 12px 12px; display: flex; width: 100%; }
    .hh-header .hh-pill__tail { margin-left: auto; }
    .hh-pill__nights { margin-left: 0; }

    /* ===== 日历底部弹层(遮罩/拖拽条/关闭按钮桌面 display:none,此处显示) ===== */
    .hh-cal-backdrop {
      display: block;
      position: fixed;
      inset: 0;
      background: rgb(0 0 0 / 0.4);
      z-index: 49;
    }
    .hh-cal-popover {
      position: fixed;
      top: auto;
      left: 0;
      right: 0;
      bottom: 0;
      max-width: none;
      max-height: 85vh;
      overflow-y: auto;
      border-radius: 16px 16px 0 0;
      padding: 8px 16px 20px;
      box-shadow: 0 -8px 24px rgb(0 0 0 / 0.12);
      z-index: 50;
    }
    .hh-cal-handle {
      display: block;
      width: 36px;
      height: 4px;
      border-radius: var(--hh-radius-full);
      background: hsl(0 0% 80%);
      margin: 4px auto 12px;
    }
    /* 关闭按钮:右上角(对齐页面 Sheet),桌面 display:none(见 styles.ts) */
    .hh-cal-close {
      position: absolute;
      top: 6px;
      right: 10px;
      z-index: 12;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      color: var(--hh-muted);
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.15s;
    }
    .hh-cal-close:hover { opacity: 1; }

    /* 开合动画(对齐页面 Sheet):data-state=open 滑入,closed 滑出后由 JS 卸载 */
    @keyframes hh-sdk-cal-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
    @keyframes hh-sdk-cal-out { from { transform: translateY(0); } to { transform: translateY(100%); } }
    @keyframes hh-sdk-cal-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes hh-sdk-cal-backdrop-out { from { opacity: 1; } to { opacity: 0; } }
    .hh-cal-popover[data-state='open'] { animation: hh-sdk-cal-in 0.25s ease-out; }
    .hh-cal-popover[data-state='closed'] { animation: hh-sdk-cal-out 0.2s ease-in forwards; }
    .hh-cal-backdrop[data-state='open'] { animation: hh-sdk-cal-backdrop-in 0.2s ease-out; }
    .hh-cal-backdrop[data-state='closed'] { animation: hh-sdk-cal-backdrop-out 0.15s ease-in forwards; }

    /* 三月竖向轮播:裁剪窗只露当月(中间),整块平移切月(见 useVerticalSwipeMonth) */
    .hh-cal-root { position: relative; }
    .hh-cal-clip { overflow: hidden; overflow: clip; }
    .hh-cal-slide { will-change: transform; }
    .hh-cal { flex-direction: column; gap: 0; }
    .hh-cal__month { width: 100%; max-width: 100%; min-width: 0; }
    /* 移动 head 仅居中标题(箭头改由浮箭头/滑动承载) */
    .hh-cal__head { justify-content: center; }
    /* 窄屏 PC(轮播 + 有鼠标)的浮箭头:裁剪窗外、浮于当月标题两侧;纯触摸机型 JS 不渲染 */
    .hh-cal__float-nav {
      position: absolute;
      top: 0;
      z-index: 10;
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--hh-border);
      border-radius: var(--hh-radius-full);
      background: var(--hh-card);
      color: var(--hh-foreground);
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
    }
    .hh-cal__float-nav:disabled { opacity: 0.35; cursor: not-allowed; }
    .hh-cal__float-nav--prev { left: 0; }
    .hh-cal__float-nav--next { right: 0; }
    /* 移动格子放大(对齐页面 h-12),触摸更友好 */
    .hh-cal__day { height: 48px; font-size: 14px; }
    /* 选中态优先于粘连 :hover:移动端 tap 后 :hover 不立即释放,基础 hover 灰(0,3,0)
       会盖过选中黑(0,1,0)致选中日变灰;此处提高选择器特异性令选中日恒为黑(对齐页面内联选中色) */
    .hh-cal__day.hh-cal__day--start:hover:not(:disabled),
    .hh-cal__day.hh-cal__day--end:hover:not(:disabled),
    .hh-cal__day.hh-cal__day--single:hover:not(:disabled) {
      background: #000000;
      color: #fff;
    }

    @media (prefers-reduced-motion: reduce) {
      .hh-cal-popover[data-state='open'],
      .hh-cal-popover[data-state='closed'],
      .hh-cal-backdrop[data-state='open'],
      .hh-cal-backdrop[data-state='closed'] { animation: none; }
    }

    /* ===== 套餐列表 ===== */
    .hh-list { gap: 12px; }
    .hh-list__loading,
    .hh-list__empty,
    .hh-list__error { padding: 32px 12px; }

    /* ===== 房型卡:全宽贴边,横向 96px 缩略图 ===== */
    .hh-room {
      padding: 10px;
      border-radius: 0;
      box-shadow: 0px 4px 34px 12px rgba(0, 0, 0, 0.06);
      // border-bottom: 1px solid var(--hh-border);
    }
    .hh-room__name { font-size: 16px; margin: 0 0 12px; }
    .hh-room__name-arrow { width: 16px; height: 16px; }
    .hh-room__body { flex-wrap: nowrap; gap: 12px; }
    .hh-room__media {
      flex: 0 0 96px;
      width: 96px;
      /* 不跟随右侧套餐列表拉伸:顶部对齐,图片固定 96×96 方图 */
      align-self: flex-start;
      display: block;
    }
    .hh-room__img {
      width: 100%;
      height: 96px;
      object-fit: cover;
      border-radius: 0;
    }
    /* PC 图片轮播在 H5 退化为 96×96 单图:容器收窄,隐藏箭头/圆点 */
    .hh-room__carousel { height: 96px; }
    .hh-room__nav,
    .hh-room__dots { display: none; }
    .hh-room__plans { flex: 1 1 0%; min-width: 0; min-height: 0; gap: 12px; border-radius: 0;}

    /* 套餐卡:收紧,内容纵向排列 */
    .hh-plan {
      flex: 0 0 auto;
      /* 允许窄屏收缩:子元素含 nowrap 文案(min-width:auto=min-content)会撑破卡片,
         显式 min-width:0 切断传导,配合 brief 限行/价格换行,杜绝横向溢出 */
      min-width: 0;
      max-width: 100%;
      padding: 12px;
      gap: 8px;
      // border-radius: var(--hh-radius-card-inner);
    }
    .hh-plan--single { min-height: 0; }
    .hh-plan--multiple { min-height: 0; }
    /* brief:H5 覆盖 PC 单行 nowrap -> 限 2 行,长文案可见且不溢出屏幕 */
    .hh-plan__brief {
      font-size: 14px;
      white-space: normal;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow-wrap: break-word;
      flex: 0 0 auto;
      min-width: 0;
      max-width: 100%;
    }
    .hh-plan__body {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
      min-width: 0;
    }
    /* 中和 PC 单/多套餐的纵向对齐规则,统一为常规列 */
    .hh-plan--single .hh-plan__body,
    .hh-plan--multiple .hh-plan__body { align-items: stretch; }
    .hh-plan--single .hh-plan__left,
    .hh-plan--multiple .hh-plan__left {
      align-self: auto;
      display: block;
      min-width: 0;
    }
    .hh-plan--single .hh-plan__right,
    .hh-plan--multiple .hh-plan__right {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      align-self: auto;
      gap: 12px;
      min-width: 0;
      flex-wrap: wrap;
    }
    .hh-plan--single .hh-plan__tags {
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
    }

    /* 标签:收紧,独占一行(必要时换行) */
    .hh-plan__tags { gap: 6px; margin-top: 4px; }
    .hh-plan__tag { font-size: 11px; padding: 2px 6px; }
    .hh-plan__tag--plain { gap: 4px; }
    .hh-plan__tag--plain svg { width: 13px; height: 13px; }

    /* 价格(左)与按钮(右)同一行、垂直居中、两侧分布;H5 隐藏划线原价 */
    .hh-plan__price { text-align: left; min-width: 0; }
    .hh-plan__price-main {
      justify-content: flex-start;
      gap: 4px;
      flex-wrap: wrap;
    }
    .hh-plan__price-num { font-size: 20px; }
    .hh-plan__price-symbol { font-size: 13px; }
    .hh-plan__price-badge { font-size: 10px; padding: 1px 2px; background: none; color: var(--hh-brand-primary); }
    .hh-plan__price-original { display: none; }
    /* "每间每晚"H5 显示:改静态内联并 order 到价格数字之后(¥599 每间每晚),
       避免绝对定位与券后价徽标重叠 */
    .hh-plan__price-num { display: inline-flex; align-items: baseline; }
    .hh-plan__price-unit {
      position: static;
      display: inline;
      order: 2;
      margin-left: 3px;
      font-size: 11px;
      font-weight: 400;
      color: var(--hh-muted);
      white-space: nowrap;
    }
    .hh-plan__book {
      align-self: center;
      padding: 6px 10px;
      font-size: 13px;
      /* 固定宽度=「立即预订」H5 自然宽(4×13px + 20px padding = 72px),与 PC 同理等宽对齐 */
      width: 72px;
      text-align: center;
      /* 保持按钮固有宽度(不缩放致文字溢出);极窄屏价格+按钮放不下时由 right flex-wrap 换行,
         margin-left:auto 使换行后按钮仍右对齐 */
      flex-shrink: 0;
      margin-left: auto;
    }
    .hh-plan__hint { font-size: 11px; margin-top: 6px; }
    .hh-plan__toggle { font-size: 13px; }

    /* 查看更多 */
    .hh-view-more { padding: 12px; font-size: 13px; }

    /* ===== 房券列表(特价预售):卡片改纵向(图在上、内容在下)、贴边、字号收紧 ===== */
    .hh-voucher { margin-top: 16px; margin-bottom: 36px; }
    /* 标题行:与页面头一致横向内边距 12px(卡片则全宽贴边,对齐 hh-room) */
    .hh-voucher__head { padding: 0 12px; margin-bottom: 12px; gap: 6px; }
    .hh-voucher__title { font-size: 18px; font-weight: 400; }
    .hh-voucher__meta { gap: 12px; font-size: 12px; }
    .hh-voucher__meta-item svg { width: 12px; height: 12px; }

    .hh-voucher__list { gap: 12px; }
    .hh-voucher__card { padding: 0; border-radius: 0; box-shadow: 0px 4px 34px 12px rgba(0, 0, 0, 0.06); }
    /* 卡片左右布局:图左内容右(替代原纵向上下布局);图片固定宽并撑满卡片高度 */
    .hh-voucher__card-body { flex-direction: row; gap: 12px; }
    .hh-voucher__media { flex: 0 0 120px; width: 120px; position: relative; }
    .hh-voucher__img {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      aspect-ratio: auto;
      object-fit: cover;
      border-radius: 0;
    }
    /* 内容区:左侧无内边距(由 card-body gap 与图片分隔),右/上下贴边 12px */
    .hh-voucher__main { padding: 12px 12px 12px 0; }

    .hh-voucher__name { font-size: 16px; }
    .hh-voucher__brief { font-size: 13px; margin-top: 6px; }
    .hh-voucher__refund { font-size: 13px; margin-top: 6px; }

    .hh-voucher__foot { margin-top: 12px; gap: 8px; }
    .hh-voucher__price-num { font-size: 20px; }
    .hh-voucher__price-symbol { font-size: 13px; }
    .hh-voucher__book { padding: 6px 14px; font-size: 13px; }

    /* ===== 房间详情弹窗 H5:底部抽屉(复刻日历浮层开合动画) ===== */
    .hh-modal__backdrop {
      background: rgb(0 0 0 / 0.4);
      -webkit-backdrop-filter: none;
      backdrop-filter: none;
    }
    .hh-modal__backdrop[data-state='open'] { animation: hh-modal-backdrop-in 0.2s ease-out; }
    .hh-modal__backdrop[data-state='closed'] { animation: hh-modal-backdrop-out 0.15s ease-in forwards; }
    /* 卡片改底部抽屉:覆盖 PC 居中 Modal 的尺寸/圆角/动画 */
    .hh-modal__card--sheet {
      position: fixed;
      top: auto;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      max-width: none;
      height: auto;
      max-height: 85vh;
      overflow-y: auto;
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -8px 24px rgb(0 0 0 / 0.12);
    }
    .hh-modal__card--sheet[data-state='open'] { animation: hh-modal-sheet-in 0.25s ease-out; }
    .hh-modal__card--sheet[data-state='closed'] { animation: hh-modal-sheet-out 0.2s ease-in forwards; }
    /* 拖拽条(仅 H5 渲染) */
    .hh-modal__handle {
      width: 36px;
      height: 4px;
      border-radius: var(--hh-radius-full);
      background: hsl(0 0% 80%);
      margin: 8px auto 12px;
    }
    /* 关闭按钮:覆盖 PC header 左侧 -> 右上角(对齐日历浮层) */
    .hh-modal__close {
      left: auto;
      top: 8px;
      right: 10px;
      transform: none;
      width: 28px;
      height: 28px;
      color: var(--hh-muted);
      opacity: 0.7;
    }
    .hh-modal__close:hover { background: transparent; opacity: 1; }
    .hh-modal__title {
      text-align: left;
      font-size: 18px;
      padding: 0 12px;
      margin: 0 0 12px;
    }
    /* H5 顶部图片轮播(复刻 RoomCard 轮播视觉) */
    .hh-modal__carousel {
      position: relative;
      width: calc(100% - 24px);
      margin: 0 12px 16px;
      height: 220px;
      overflow: hidden;
      border-radius: var(--hh-radius-card);
      background: var(--hh-bg);
    }
    .hh-modal__carousel--empty {
      display: flex;
      align-items: center;
      justify-content: center;
      color: hsl(0 0% 60%);
    }
    .hh-modal__carousel-track {
      display: flex;
      height: 100%;
      transition: transform 0.3s ease;
    }
    .hh-modal__carousel-img {
      flex: 0 0 100%;
      width: 100%;
      height: 100%;
      object-fit: cover;
      -webkit-user-drag: none;
    }
    .hh-modal__carousel-nav {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 20;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: var(--hh-radius-full);
      background: rgba(255, 255, 255, 0.9);
      color: hsl(0 0% 25%);
      box-shadow: 0 1px 4px rgb(0 0 0 / 0.15);
      cursor: pointer;
    }
    .hh-modal__carousel-nav--prev { left: 8px; }
    .hh-modal__carousel-nav--next { right: 8px; }
    .hh-modal__carousel-dots {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 8px;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }
    .hh-modal__carousel-dot {
      width: 6px;
      height: 6px;
      padding: 0;
      border: none;
      border-radius: var(--hh-radius-full);
      background: rgba(255, 255, 255, 0.5);
      cursor: pointer;
      transition: width 0.2s, background 0.2s;
    }
    .hh-modal__carousel-dot--active {
      width: 16px;
      background: #fff;
    }
    /* 设施区:全宽(覆盖 PC 50%) */
    .hh-modal__facilities {
      width: 100%;
      padding: 0 12px 20px;
    }
    .hh-modal__group-title { font-size: 15px; }
    .hh-modal__group-items { gap: 10px; }
    .hh-modal__facility { font-size: 13px; }
    @keyframes hh-modal-sheet-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
    @keyframes hh-modal-sheet-out { from { transform: translateY(0); } to { transform: translateY(100%); } }
    @keyframes hh-modal-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes hh-modal-backdrop-out { from { opacity: 1; } to { opacity: 0; } }

    /* H5:红包弹框改底部抽屉(复用 sheet 模式 + 动画);登录弹框保持 PC 居中(用户要求 H5 不抽屉) */
    .hh-modal__card--coupon {
      position: fixed;
      top: auto;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      max-width: none;
      height: auto;
      min-height: 30vh;
      max-height: 80vh;
      overflow: hidden;
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -8px 24px rgb(0 0 0 / 0.12);
    }
    .hh-modal__card--coupon[data-state='open'] { animation: hh-modal-sheet-in 0.25s ease-out; }
    .hh-modal__card--coupon[data-state='closed'] { animation: hh-modal-sheet-out 0.2s ease-in forwards; }
    /* 关闭按钮:右上角(对齐 sheet) */
    .hh-coupon-modal__close {
      top: 8px;
      right: 10px;
      width: 28px;
      height: 28px;
      color: var(--hh-muted);
      opacity: 0.7;
    }
    .hh-coupon-modal__close:hover { background: transparent; opacity: 1; }
    /* 红包弹框:贴边收紧(卡片高度规则见上方抽屉块) */
    .hh-coupon-modal__title { padding: 16px 16px 10px; font-size: 16px; }
    .hh-coupon-modal__banner { padding: 0 12px; }
    .hh-coupon-modal__banner-img--pc { display: none; }
    .hh-coupon-modal__banner-img--mobile { display: block; }
    .hh-coupon-modal__scroll { padding: 10px 12px; }
    .hh-coupon-modal__footer { padding: 10px 12px 14px; }
    .hh-coupon-card__value { width: 84px; }
    .hh-coupon-card__big { font-size: 18px; }
    .hh-coupon-modal__submit { height: 44px; font-size: 15px; }

    @media (prefers-reduced-motion: reduce) {
      .hh-modal__backdrop[data-state='open'],
      .hh-modal__backdrop[data-state='closed'],
      .hh-modal__card--sheet[data-state='open'],
      .hh-modal__card--sheet[data-state='closed'],
      .hh-modal__card--login[data-state='open'],
      .hh-modal__card--login[data-state='closed'],
      .hh-modal__card--coupon[data-state='open'],
      .hh-modal__card--coupon[data-state='closed'] { animation: none; }
    }
  }
`;
